'use client';

import { useEffect, useState, useTransition, type ReactNode } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useVerifyOtp } from '@kit/supabase/hooks/use-verify-otp';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@kit/ui/input-otp';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../captcha/client';
import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import { useOtpSignUpFlow } from '../hooks/use-otp-sign-up-flow';
import type { SignupCustomField } from '../schemas/signup-custom-fields.schema';
import { associateCustomerWithTenantAction } from '../server/associate-customer-action';
import {
  identifyAccountAction,
  type IdentifyAccountResult,
} from '../server/identify-account-action';
import { resendEmailOtpAction } from '../server/resend-email-otp-action';
import { AuthErrorAlert } from './auth-error-alert';
import { EmailIdentifyForm } from './email-identify-form';
import { OtpSignUpForm } from './otp-sign-up-form';
import { PhoneMismatchBanner } from './phone-mismatch-banner';
import { signupProfileSetupPath } from './signup-custom-fields-input';
import { SignupPhoneVerifyStep } from './signup-phone-verify-step';

type OtpSignInContainerProps = {
  shouldCreateUser: boolean;
  captchaSiteKey?: string;
  signUpPath?: string;
  displayTermsCheckbox?: boolean;
  signupCustomFields?: SignupCustomField[];
  displayChildrenSection?: boolean;
  defaultValues?: {
    email: string;
    name?: string;
  };
  phoneHint?: string;
  // Rendered below the FIRST screen only (identify / sign-up form) — the
  // methods container passes its alternative-methods section here so the
  // switcher disappears once the user has progressed into the flow.
  footer?: ReactNode;
};

/**
 * Email-OTP container. At parity with the password flow:
 *  - sign-in (shouldCreateUser=false): email identify → route to sign-up for
 *    guest/unknown, otherwise send a branded 6-digit code → verify.
 *  - sign-up (shouldCreateUser=true): collect name + email + phone (+ children),
 *    resolve/upgrade the account via signUpWithOtpAction (guest-upgrade +
 *    email/phone mismatch banner + structured errors), then verify the code.
 *
 * The code is delivered as a CODE (not a magic link) because the send carries the
 * `auth_flow=otp` marker on emailRedirectTo, which the Send Email hook detects.
 * Verification uses type:'email' to match the signInWithOtp-minted token.
 */
export function OtpSignInContainer(props: OtpSignInContainerProps) {
  if (props.shouldCreateUser) {
    return <OtpSignUpContainer {...props} />;
  }

  return <OtpSignInOnlyContainer {...props} />;
}

// ---------------------------------------------------------------------------
// Sign-in: email identify → (route to sign-up | send code) → verify
// ---------------------------------------------------------------------------

function OtpSignInOnlyContainer({
  captchaSiteKey,
  signUpPath = '/auth/sign-up',
  footer,
}: OtpSignInContainerProps) {
  const router = useRouter();
  const params = useSearchParams();
  const captcha = useCaptcha({ siteKey: captchaSiteKey });
  const monitoring = useMonitoring();
  const [identifying, startIdentify] = useTransition();
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null);
  const [lastEmail, setLastEmail] = useState('');
  const [sendError, setSendError] = useState<Error | null>(null);

  const routeToSignUp = (email: string, hint?: IdentifyAccountResult) => {
    // method=otp keeps the sign-up page on the email-code flow the user chose
    // here — without it the sign-up container defaults to email & password.
    const search = new URLSearchParams({ email, method: 'otp' });
    if (hint?.kind === 'guest_upgrade') {
      if (hint.name) search.set('name', hint.name);
      if (hint.phoneHint) search.set('phone_hint', hint.phoneHint);
    }
    router.push(`${signUpPath}?${search.toString()}`);
  };

  const onIdentify = (values: { email: string }) => {
    setLastEmail(values.email);
    setSendError(null);

    startIdentify(async () => {
      try {
        const result = await identifyAccountAction({ email: values.email });

        // Guest with no password / unknown email → send them through sign-up so
        // the guest-upgrade + mismatch logic can run (parity with password flow).
        if (result.kind === 'guest_upgrade' || result.kind === 'unknown') {
          routeToSignUp(values.email, result);
          return;
        }

        // Existing usable account → send the branded code and move to verify.
        const sent = await resendEmailOtpAction({ email: values.email });

        if (!sent.success) {
          setSendError(new Error('otpSendFailed'));
          return;
        }

        setVerifyEmail(values.email);
      } catch (error) {
        monitoring.captureException(
          error instanceof Error
            ? error
            : new Error('Identify account failed (otp)'),
        );
        setSendError(new Error('otpSendFailed'));
      }
    });
  };

  if (verifyEmail) {
    return (
      <OtpVerifyForm
        email={verifyEmail}
        next={params.get('next') ?? '/home'}
        onUseDifferentEmail={() => {
          setVerifyEmail(null);
        }}
      />
    );
  }

  return (
    <>
      <AuthErrorAlert error={sendError} />

      <EmailIdentifyForm
        onSubmit={onIdentify}
        loading={identifying}
        defaultEmail={lastEmail}
        captchaField={captcha.field}
      />

      {footer}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sign-up: full form → (mismatch banner | errors) → code → verify
// ---------------------------------------------------------------------------

function OtpSignUpContainer({
  captchaSiteKey,
  displayTermsCheckbox,
  displayChildrenSection,
  signupCustomFields,
  defaultValues,
  phoneHint,
  footer,
}: OtpSignInContainerProps) {
  const params = useSearchParams();
  const captcha = useCaptcha({ siteKey: captchaSiteKey });
  const captchaLoading = !captcha.isReady;

  const {
    signUp,
    loading,
    error,
    signedUpEmail,
    emailMismatch,
    resolveEmailMismatch,
    dismissEmailMismatch,
    phoneMismatch,
    resolvePhoneMismatch,
    dismissPhoneMismatch,
  } = useOtpSignUpFlow({
    captchaToken: captcha.token,
    resetCaptchaToken: captcha.reset,
  });

  if (signedUpEmail) {
    return (
      <OtpVerifyForm
        email={signedUpEmail}
        next={signupProfileSetupPath(params.get('next'))}
        offerPhoneVerify
      />
    );
  }

  return (
    <>
      <AuthErrorAlert error={error} />

      <If condition={Boolean(emailMismatch)}>
        {emailMismatch ? (
          <EmailMismatchBanner
            emailHint={emailMismatch.emailHint}
            typedEmail={emailMismatch.credentials.email}
            loading={loading}
            onUseExisting={() => resolveEmailMismatch('use-existing')}
            onOverride={() => resolveEmailMismatch('override')}
            onDismiss={dismissEmailMismatch}
          />
        ) : null}
      </If>

      <If condition={Boolean(phoneMismatch)}>
        {phoneMismatch ? (
          <PhoneMismatchBanner
            phoneHint={phoneMismatch.phoneHint}
            typedPhone={phoneMismatch.credentials.phoneNumber ?? ''}
            loading={loading}
            onUseExisting={() => resolvePhoneMismatch('use-existing')}
            onOverride={() => resolvePhoneMismatch('override')}
            onDismiss={dismissPhoneMismatch}
          />
        ) : null}
      </If>

      <If condition={!emailMismatch && !phoneMismatch && Boolean(phoneHint)}>
        <div className="border-primary/30 bg-primary/5 rounded-lg border p-3 text-sm">
          <p className="text-foreground font-medium">
            {defaultValues?.name ? (
              <Trans
                i18nKey="auth:welcomeBackNamed"
                values={{ name: defaultValues.name }}
                defaults="Welcome back, {{name}}"
              />
            ) : (
              <Trans i18nKey="auth:welcomeBack" defaults="Welcome back" />
            )}
          </p>

          <p className="text-muted-foreground mt-1">
            <Trans
              i18nKey="auth:welcomeBackBodyOtp"
              values={{ phoneHint }}
              components={{ strong: <strong className="text-foreground" /> }}
              defaults="We have a profile linked to <strong>{{phoneHint}}</strong>. Confirm your details and we'll send a verification code to finish setting up your account."
            />
          </p>
        </div>
      </If>

      <div>
        <OtpSignUpForm
          onSubmit={signUp}
          loading={loading}
          defaultValues={defaultValues}
          displayTermsCheckbox={displayTermsCheckbox}
          displayChildrenSection={displayChildrenSection}
          signupCustomFields={signupCustomFields}
          captchaLoading={captchaLoading}
        />

        {captcha.field}
      </div>

      {footer}
    </>
  );
}

// Shared with password flow's banner shape, with OTP-specific copy: we send a
// verification code rather than ask for a password.
function EmailMismatchBanner({
  emailHint,
  typedEmail,
  loading,
  onUseExisting,
  onOverride,
  onDismiss,
}: {
  emailHint: string;
  typedEmail: string;
  loading: boolean;
  onUseExisting: () => void;
  onOverride: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="border-primary/30 bg-primary/5 rounded-lg border p-4 text-sm">
      <p className="text-foreground font-medium">
        <Trans i18nKey="auth:emailMismatchHeading" defaults="Is this you?" />
      </p>

      <p className="text-muted-foreground mt-1">
        <Trans
          i18nKey="auth:emailMismatchBody"
          values={{ emailHint, typedEmail }}
          components={{ strong: <strong className="text-foreground" /> }}
          defaults="We already have a profile on this phone number linked to <strong>{{emailHint}}</strong>. You typed <strong>{{typedEmail}}</strong>. Which email do you want to use?"
        />
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={loading}
          onClick={onUseExisting}
          className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50"
          data-test="email-mismatch-use-existing"
        >
          <Trans
            i18nKey="auth:emailMismatchUseExisting"
            defaults="Use the email on file"
          />
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={onOverride}
          className="border-input bg-background hover:bg-accent inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
          data-test="email-mismatch-override"
        >
          <Trans
            i18nKey="auth:emailMismatchOverride"
            defaults="No, use the new email"
          />
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded-md px-3 py-2 text-sm disabled:opacity-50"
        >
          <Trans i18nKey="common:cancel" defaults="Cancel" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared verify step (6-digit code → verifyOtp type:'email')
// ---------------------------------------------------------------------------

const OtpSchema = z.object({ token: z.string().min(6).max(6) });

function OtpVerifyForm({
  email,
  next,
  onUseDifferentEmail,
  offerPhoneVerify = false,
}: {
  email: string;
  next: string;
  onUseDifferentEmail?: () => void;
  // Sign-up only: after the email verifies, offer the skippable WhatsApp
  // verify step (self-skipping when no phone was typed / already confirmed).
  offerPhoneVerify?: boolean;
}) {
  const verifyMutation = useVerifyOtp();
  const router = useRouter();
  const { recordAuthMethod } = useLastAuthMethod();
  const [resendCooldown, setResendCooldown] = useState(30);
  const [resending, setResending] = useState(false);
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const form = useForm({
    resolver: zodResolver(OtpSchema),
    defaultValues: { token: '' },
  });

  const handleVerify = async ({ token }: z.infer<typeof OtpSchema>) => {
    // type:'email' matches the token minted by signInWithOtp({ email }).
    await verifyMutation.mutateAsync({ type: 'email', email, token });

    recordAuthMethod('otp', { email });

    // Tenant association runs in the background; non-critical.
    associateCustomerWithTenantAction().catch(() => {});

    if (offerPhoneVerify) {
      setShowPhoneVerify(true);
      return;
    }

    router.replace(next);
  };

  const [resendError, setResendError] = useState<Error | null>(null);

  const handleResend = async () => {
    setResending(true);
    setResendError(null);

    try {
      const result = await resendEmailOtpAction({ email });

      if (!result.success) {
        setResendError(new Error('otpSendFailed'));
        return;
      }

      setResendCooldown(30);
      form.setValue('token', '');
    } catch {
      setResendError(new Error('otpSendFailed'));
    } finally {
      setResending(false);
    }
  };

  if (showPhoneVerify) {
    return <SignupPhoneVerifyStep onDone={() => router.replace(next)} />;
  }

  return (
    <Form {...form}>
      <form
        className="flex w-full flex-col items-center space-y-8"
        onSubmit={form.handleSubmit(handleVerify)}
      >
        <AuthErrorAlert error={verifyMutation.error ?? resendError} />

        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey="auth:otpSentToEmail"
              defaults="We sent a verification code to <strong>{{email}}</strong>"
              values={{ email }}
              components={{ strong: <strong className="text-foreground" /> }}
            />
          </p>
        </div>

        <FormField
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  disabled={verifyMutation.isPending}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} data-slot="0" />
                    <InputOTPSlot index={1} data-slot="1" />
                    <InputOTPSlot index={2} data-slot="2" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} data-slot="3" />
                    <InputOTPSlot index={4} data-slot="4" />
                    <InputOTPSlot index={5} data-slot="5" />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>

              <FormDescription>
                <Trans i18nKey="common:otp.enterCodeFromEmail" />
              </FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex w-full flex-col gap-y-2">
          <Button
            type="submit"
            disabled={verifyMutation.isPending}
            data-test="otp-verify-button"
          >
            {verifyMutation.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                <Trans i18nKey="common:otp.verifying" />
              </>
            ) : (
              <Trans i18nKey="common:otp.verifyCode" />
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            disabled={
              verifyMutation.isPending || resending || resendCooldown > 0
            }
            onClick={handleResend}
          >
            {resending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                <Trans i18nKey="common:otp.sendingCode" />
              </>
            ) : resendCooldown > 0 ? (
              <Trans
                i18nKey="common:otp.resendCodeIn"
                values={{ seconds: resendCooldown }}
              />
            ) : (
              <Trans i18nKey="common:otp.requestNewCode" />
            )}
          </Button>

          <If condition={Boolean(onUseDifferentEmail)}>
            <Button
              type="button"
              variant="link"
              size="sm"
              disabled={verifyMutation.isPending}
              onClick={onUseDifferentEmail}
            >
              <Trans
                i18nKey="auth:useDifferentEmail"
                defaults="Use a different email"
              />
            </Button>
          </If>
        </div>
      </form>
    </Form>
  );
}
